/**
 * Pure haversine wedge-to-GeoJSON projection helper (Phase 13 Plan 13-06 / REP-08 SPA half).
 *
 * Projects an antenna coverage wedge from a sentry's geographic location
 * into a GeoJSON Polygon suitable for rendering as a maplibre fill layer
 * on the satellite map. The wedge is defined by:
 *   - centroid (sentry lat, lng) in WGS84 decimal degrees
 *   - antenna bearing (deg, 0 = North, 90 = East, clockwise positive)
 *   - antenna beamwidth (deg; full angular width, wedge centered on bearing)
 *   - range_m (radial distance from the sentry in meters)
 *
 * Math:
 *   - Great-circle (haversine) destination-point formula. Earth modeled as a
 *     sphere of radius 6371000 m. Accurate to ~0.5% for ranges under 1 km;
 *     fine for the SignalSentry use case (typical zone ranges 30-150 m).
 *   - Wedge polygon is built as: [sentry centroid, N arc points spanning the
 *     half-beamwidth, sentry centroid (close the ring)]. The arc points are
 *     evenly spaced in bearing degrees from (bearing - beamwidth/2) to
 *     (bearing + beamwidth/2). With N points and N-1 intervals, fraction at
 *     index i is i / (N - 1).
 *   - 360-wrap edge case: bearings handed to the destination-point formula
 *     are passed through Math.cos/Math.sin via toRad(), which handle
 *     negative degrees and degrees > 360 naturally.
 *   - Output vertex count: 1 (centroid) + N (arc) + 1 (close) = N + 2. With
 *     the default N = 16, the polygon has 18 vertices, which renders as a
 *     visually smooth arc at typical satellite map zoom levels.
 *
 * Edge cases:
 *   - range_m = 0 collapses every arc vertex to the centroid (no exception).
 *   - GeoJSON Polygon spec requires the ring to be closed (last vertex ==
 *     first). The trailing centroid push satisfies this.
 *   - Longitude wrap at +/- 180 is normalized via the ((deg + 540) % 360) -
 *     180 idiom. Not exercised in Pantex use cases but kept for robustness.
 *
 * The helper is consumed by:
 *   - SatelliteSentryMap.tsx zone-overlay effect (renders one fill polygon
 *     per zone with a data-driven fill-color expression keyed on smoothed
 *     RSSI dBm).
 *
 * Plan reference: 13-06-PLAN.md tasks/Task1 + interfaces sketch.
 */

// ---------------------------------------------------------------------- constants --

/**
 * Earth radius in meters, sphere model. WGS84 ellipsoid would give a more
 * accurate result but for ranges under 1 km the sphere model is within 0.5%
 * which beats the precision of the input bearing/beamwidth from a typical
 * antenna calibration.
 */
const EARTH_RADIUS_M = 6_371_000;

/**
 * Default number of arc points to sample along the wedge boundary. 16 ->
 * 18 vertices total -> smooth at every satellite map zoom level we care
 * about. Raise via the `arcSteps` parameter if very wide beams (> 180 deg)
 * need extra smoothness.
 */
const DEFAULT_ARC_STEPS = 16;

/**
 * Phase 14 Plan 14-03 / CFG-01 — TEMPORARY cloud-side fallback for
 * `zones.range_m` when the on-site box's /api/zones response omits the
 * field.
 *
 * Background:
 *   - On-site migration 047 (2026-05-20) added the column with
 *     `DEFAULT 50.0`, so the data exists in the DB.
 *   - However, the on-site `/api/zones` row serializer does NOT (yet)
 *     project `range_m` onto the wire payload. Until the on-site team
 *     ships CFG-02 (a 5-line edit to their `signalsentry-api/routers/
 *     zones.py` row builder), the cloud SPA receives rows without the
 *     field and the satellite zone overlay would otherwise collapse
 *     wedges to a zero radius.
 *   - CFG-01 fixes the symptom on the cloud side: a single fallback
 *     point that every zone-consumer in the SPA reads through.
 *
 * Why centralize: there are TWO consumer call sites today
 * (`AlertReplayPage.tsx::satelliteZoneOverlay` memo +
 * `useSiteZones.ts::coerceZone` helper). Inlining `?? 50` at each site
 * means a third future consumer could silently inline a different
 * default (or none); centralizing keeps all consumers honest.
 *
 * Lifecycle:
 *   - When CFG-02 lands and the on-site serializer projects `range_m`,
 *     this fallback continues to apply for OLDER deployments still
 *     running pre-CFG-02 builds (graceful degrade). Once every on-site
 *     box has shipped CFG-02 AND the cloud has bumped a contract
 *     requiring `range_m`, this constant + helper can be deleted in
 *     ONE place.
 *
 * Upstream tracking: CFG-02 (REMOTE) in
 * `signalsentry-fleet/.planning/phases/14-.../14-CONTEXT.md`.
 */
export const FALLBACK_RANGE_M = 50;

/**
 * Coerce a possibly-missing `zones.range_m` into a valid positive radius.
 *
 * Returns the input ONLY when it is a finite number strictly greater
 * than zero; otherwise returns {@link FALLBACK_RANGE_M}. Accepts the
 * full union of upstream shapes:
 *   - `number` -> validated then returned (or fallback if non-finite /
 *     non-positive).
 *   - `null` / `undefined` (field omitted on the wire) -> fallback.
 *   - `NaN` / `+Infinity` / `-Infinity` -> fallback.
 *
 * @param value Raw `range_m` from the on-site `/api/zones` response row.
 * @returns A safe positive meter value for downstream projection.
 */
export function coerceRangeM(value: number | null | undefined): number {
  if (typeof value !== 'number') return FALLBACK_RANGE_M;
  if (!Number.isFinite(value)) return FALLBACK_RANGE_M;
  if (value <= 0) return FALLBACK_RANGE_M;
  return value;
}

// -------------------------------------------------------------------------- types --

/**
 * Minimal GeoJSON Polygon shape. We do not depend on @types/geojson because
 * the only consumer (maplibre's addSource) accepts a plain object literal
 * shaped this way. Vendoring the inline type avoids a 6 kB type-package
 * dependency for one helper.
 */
export interface GeoJSONPolygon {
  type: 'Polygon';
  /**
   * One or more linear rings. The first ring is the outer boundary;
   * additional rings (not used here) would be holes. Each ring is an array
   * of [lng, lat] pairs (GeoJSON ordering, NOT [lat, lng]).
   */
  coordinates: Array<Array<[number, number]>>;
}

// ----------------------------------------------------------------- math helpers --

/**
 * Convert degrees to radians.
 *
 * @param deg Angle in degrees.
 * @returns Angle in radians.
 */
function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

/**
 * Convert radians to degrees.
 *
 * @param rad Angle in radians.
 * @returns Angle in degrees.
 */
function toDeg(rad: number): number {
  return (rad * 180) / Math.PI;
}

/**
 * Haversine destination-point formula. Given a starting point on a sphere
 * (lat, lng), a compass bearing in degrees (0 = N, 90 = E, clockwise),
 * and a distance in meters, returns the destination [lat, lng] in degrees.
 *
 * Formula reference: https://www.movable-type.co.uk/scripts/latlong.html
 * (Chris Veness; public domain). The Math.atan2 / Math.asin combination
 * handles the polar singularity correctly for non-polar latitudes.
 *
 * @param lat Starting latitude in WGS84 decimal degrees.
 * @param lng Starting longitude in WGS84 decimal degrees.
 * @param bearingDeg Compass bearing in degrees (0 = N, 90 = E, CW positive).
 * @param distanceM Distance in meters (must be >= 0).
 * @returns [lat, lng] of the destination point in WGS84 decimal degrees.
 */
function destinationPoint(
  lat: number,
  lng: number,
  bearingDeg: number,
  distanceM: number,
): [number, number] {
  // distanceM = 0 -> the destination IS the start point. Short-circuit so
  // the arc points all coincide with the centroid (degenerate polygon).
  if (distanceM === 0) {
    return [lat, lng];
  }

  const bearing = toRad(bearingDeg);
  const latRad = toRad(lat);
  const lngRad = toRad(lng);
  const angularDistance = distanceM / EARTH_RADIUS_M;

  const lat2 = Math.asin(
    Math.sin(latRad) * Math.cos(angularDistance) +
      Math.cos(latRad) * Math.sin(angularDistance) * Math.cos(bearing),
  );

  const lng2 =
    lngRad +
    Math.atan2(
      Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(latRad),
      Math.cos(angularDistance) - Math.sin(latRad) * Math.sin(lat2),
    );

  // Normalize longitude to (-180, 180].
  const normalizedLngDeg = ((toDeg(lng2) + 540) % 360) - 180;

  return [toDeg(lat2), normalizedLngDeg];
}

// ----------------------------------------------------------------- public API --

/**
 * Project an antenna coverage wedge to a GeoJSON Polygon.
 *
 * The polygon has 1 + arcSteps + 1 = arcSteps + 2 vertices total. Vertex
 * ordering: [centroid, arc_0, arc_1, ..., arc_{N-1}, centroid] where the
 * arc points span from (bearing - beamwidth/2) to (bearing + beamwidth/2)
 * inclusive at both endpoints (so arc_0 sits on the start-bearing edge
 * and arc_{N-1} sits on the end-bearing edge).
 *
 * @param centroidLat Sentry latitude in WGS84 decimal degrees.
 * @param centroidLng Sentry longitude in WGS84 decimal degrees.
 * @param bearingDeg Antenna boresight bearing (0 = N, 90 = E, CW positive).
 * @param beamwidthDeg Antenna full beamwidth in degrees (wedge is centered
 *   on the bearing).
 * @param rangeM Wedge radial distance in meters. Must be >= 0. A value of
 *   0 collapses every arc point to the centroid (degenerate polygon; valid
 *   GeoJSON but renders nothing).
 * @param arcSteps Number of arc sample points (default 16). The total
 *   polygon vertex count is arcSteps + 2.
 * @returns A GeoJSON Polygon ready to be set on a maplibre GeoJSON source.
 */
export function projectWedge(
  centroidLat: number,
  centroidLng: number,
  bearingDeg: number,
  beamwidthDeg: number,
  rangeM: number,
  arcSteps: number = DEFAULT_ARC_STEPS,
): GeoJSONPolygon {
  // 2026-05-30: omni / full-circle zones (beamwidth >= 360) must NOT carry
  // the two straight "pie slice" edges. At a full 360 those edges both land on
  // the same bearing and overlap into a single visible center-to-rim radius
  // line that reads as a stray spoke through the disc. Render a closed RING of
  // rim points with NO centroid vertices instead, so the outline is a clean
  // circle (the fill is a full disc). Directional wedges (< 360) keep their
  // edges - those are the intended slice sides.
  if (beamwidthDeg >= 360) {
    // Sample more points than the wedge default so a full circle reads round
    // rather than polygonal at typical satellite zoom levels.
    const circleSteps = Math.max(arcSteps, 48);
    const ring: Array<[number, number]> = new Array(circleSteps + 1);
    for (let i = 0; i < circleSteps; i++) {
      const arcBearing = (360 * i) / circleSteps;
      const [lat, lng] = destinationPoint(
        centroidLat,
        centroidLng,
        arcBearing,
        rangeM,
      );
      ring[i] = [lng, lat];
    }
    // Close the ring back to the first rim point (GeoJSON requires a closed
    // ring); deliberately NOT the centroid, so there is no spoke.
    ring[circleSteps] = ring[0];
    return { type: 'Polygon', coordinates: [ring] };
  }

  const halfBeam = beamwidthDeg / 2;
  const startBearing = bearingDeg - halfBeam;
  const endBearing = bearingDeg + halfBeam;

  // Pre-size the ring: 1 (centroid) + arcSteps (arc) + 1 (close) = arcSteps + 2.
  const ring: Array<[number, number]> = new Array(arcSteps + 2);
  ring[0] = [centroidLng, centroidLat];

  // Spread arcSteps points across [startBearing, endBearing] inclusive at
  // both endpoints. With N points and (N-1) intervals, fraction at index i
  // is i / (N - 1). When arcSteps === 1 (degenerate but allowed), the
  // single arc point sits on the boresight.
  const denom = arcSteps > 1 ? arcSteps - 1 : 1;
  for (let i = 0; i < arcSteps; i++) {
    const fraction = i / denom;
    const arcBearing = startBearing + (endBearing - startBearing) * fraction;
    const [lat, lng] = destinationPoint(centroidLat, centroidLng, arcBearing, rangeM);
    ring[i + 1] = [lng, lat];
  }

  // Close the ring by returning to the centroid.
  ring[arcSteps + 1] = [centroidLng, centroidLat];

  return {
    type: 'Polygon',
    coordinates: [ring],
  };
}
